import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CreditCardOffRoundedIcon from "@mui/icons-material/CreditCardOffRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { auth } from "../firebase";
import api from "../services/api";

const SESSION_KEY = "sb_payment_modal_dismissed";

export default function PaymentFailedModal({ subscriptionStatus, isPro }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isCanceled = subscriptionStatus === "canceled" && !isPro;
  const isPastDue = subscriptionStatus === "past_due";
  const shouldShow = isPastDue || isCanceled;

  // Show on every fresh login session, but not on every page navigation
  useEffect(() => {
    if (!shouldShow) {
      // Payment fixed — clear the dismissed flag so it shows again if it ever happens again
      sessionStorage.removeItem(SESSION_KEY);
      setOpen(false);
      return;
    }
    const dismissed = sessionStorage.getItem(SESSION_KEY);
    if (!dismissed) setOpen(true);
  }, [shouldShow, subscriptionStatus]);

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
  };

  const handlePortal = async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await api.post(
        "/stripe/create-portal-session",
        { uid: auth.currentUser.uid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Portal error:", err);
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await api.post(
        "/stripe/create-checkout-session",
        {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email,
          source: "payment_failed_modal",
          planType: "pro",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setLoading(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Coloured top bar */}
      <Box
        sx={{
          height: 6,
          background: isPastDue
            ? "linear-gradient(90deg, #f97316, #ef4444)"
            : "linear-gradient(90deg, #ef4444, #7f1d1d)",
        }}
      />

      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        {/* Icon */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: isPastDue
              ? alpha("#f97316", 0.12)
              : alpha("#ef4444", 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
          }}
        >
          {isPastDue ? (
            <CreditCardOffRoundedIcon
              sx={{ fontSize: 32, color: "#f97316" }}
            />
          ) : (
            <WarningAmberRoundedIcon
              sx={{ fontSize: 32, color: "#ef4444" }}
            />
          )}
        </Box>

        {/* Heading */}
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ mb: 1, letterSpacing: -0.5, color: "text.primary" }}
        >
          {isPastDue
            ? "Your payment didn't go through"
            : "Your subscription has ended"}
        </Typography>

        {/* Body */}
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, lineHeight: 1.7 }}
        >
          {isPastDue ? (
            <>
              We couldn't charge your card for your SortlyBooks Pro subscription.{" "}
              <strong>Your data is safe</strong> and you still have access for now,
              but please update your payment details to avoid losing Pro features.
              Stripe will retry automatically once your card is updated.
            </>
          ) : (
            <>
              Your Pro subscription has been cancelled and your account is now on
              the free plan. Re-subscribe below to restore full access — all your
              data is still here waiting for you.
            </>
          )}
        </Typography>

        {/* What happens next */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: isPastDue
              ? alpha("#f97316", 0.07)
              : alpha("#ef4444", 0.07),
            border: `1px solid ${
              isPastDue ? alpha("#f97316", 0.2) : alpha("#ef4444", 0.2)
            }`,
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {isPastDue ? "What to do:" : "What you're missing:"}
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            {isPastDue ? (
              <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 2 }}>
                <li>Click <strong>"Update payment method"</strong> below</li>
                <li>Update your card in the secure Stripe portal</li>
                <li>Return here — your Pro access resumes automatically</li>
              </ol>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 2 }}>
                <li>PDF report generation</li>
                <li>Automatic report delivery to your accountant</li>
                <li>Unlimited receipts & invoices</li>
              </ul>
            )}
          </Typography>
        </Box>

        {/* CTAs */}
        <Stack spacing={1.5}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            endIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : isPastDue ? (
                <ArrowForwardRoundedIcon />
              ) : (
                <RefreshRoundedIcon />
              )
            }
            onClick={isPastDue ? handlePortal : handleResubscribe}
            sx={{
              py: 1.5,
              fontWeight: 700,
              fontSize: "1rem",
              borderRadius: 2.5,
              textTransform: "none",
              background: isPastDue
                ? "linear-gradient(90deg, #f97316, #ef4444)"
                : "linear-gradient(90deg, #6366f1, #1e3a8a)",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              },
            }}
          >
            {loading
              ? "Redirecting…"
              : isPastDue
              ? "Update payment method"
              : "Restart Pro subscription — €29.99/mo"}
          </Button>

          <Button
            fullWidth
            variant="text"
            size="medium"
            onClick={handleDismiss}
            sx={{
              fontWeight: 500,
              color: "text.secondary",
              textTransform: "none",
              "&:hover": { bgcolor: "transparent", color: "text.primary" },
            }}
          >
            {isPastDue ? "Remind me later" : "Continue on free plan"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
